/**
 * DataQualityWidget
 *
 * Dashboard homepage widget showing open data quality issues.
 * Displays count badge and up to 5 items sorted by creation date.
 * Links to data quality tools for resolution.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 19, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Database,
  AlertTriangle,
  ArrowRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface DataQualityIssue {
  id: string;
  issueType: string;
  severity: string;
  description: string;
  contentType: string;
  contentId: string;
  createdAt: string;
}

export function DataQualityWidget() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [issues, setIssues] = useState<DataQualityIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchIssues();
    }
  }, [selectedSite?.id]);

  const fetchIssues = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/content-evaluations/data-quality-issues?siteId=${selectedSite.id}`);
      const data = await response.json();
      if (data.success) {
        setIssues(data.data || []);
      } else {
        setError('Failed to load data quality issues');
      }
    } catch (err) {
      console.error('[DataQualityWidget] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'text-red-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const handleItemClick = (issue: DataQualityIssue) => {
    // Navigate to data quality audit with the issue context
    navigate(`/research/data-quality-audit?issueId=${issue.id}&contentType=${issue.contentType}&contentId=${issue.contentId}`);
  };

  const handleViewAll = () => {
    navigate('/research/data-quality-audit');
  };

  if (!selectedSite?.id) {
    return null;
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4" />
            Data Quality Issues
          </CardTitle>
          <Badge variant="secondary">Error</Badge>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchIssues}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Database className="h-4 w-4" />
          Data Quality Issues
        </CardTitle>
        {loading ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          <Badge variant={issues.length > 0 ? 'destructive' : 'secondary'}>
            {issues.length}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        ) : issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              No data quality issues!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {issues.slice(0, 5).map((issue) => (
              <div
                key={issue.id}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer transition-colors"
                onClick={() => handleItemClick(issue)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleItemClick(issue);
                  }
                }}
              >
                <AlertTriangle className={`h-4 w-4 ${getSeverityColor(issue.severity)}`} />
                <span className="flex-1 text-sm truncate" title={issue.description}>
                  {issue.description}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(issue.createdAt), { addSuffix: true })}
                </span>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full mt-3"
          onClick={handleViewAll}
        >
          View All Issues
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default DataQualityWidget;
